import React, { useState, useEffect, useCallback } from 'react';
import { GraduationCap, BookOpen, CheckCircle2, Lock, FileText, HelpCircle, Award, ChevronLeft, ChevronRight, ArrowLeft, Calendar, MessageCircle, Download, ExternalLink, Target, Zap, Flag, Clock, Users, Video, Play, XCircle, RefreshCw, Trash2, CalendarDays } from 'lucide-react';
import { MiniAppShell } from './MiniAppShell';
import { useBizBuildingProgram } from '../api/useBizBuildingProgram';
import { useBilling } from '../../../context/BillingContext';
import { useChat } from '../../../context/ChatContext';
import { config } from '../../../config';
import { BookingCalendar } from '../../../components/scheduling/BookingCalendar';
import { CommunityHub } from '../../../components/community/CommunityHub';
import { WelcomeVideoBanner } from '../../../components/program/WelcomeVideoBanner';
import { type Thread } from '../../../components/community/ForumThread';

interface BizBuildingAppProps {
    onExecutionStarted: (executionId: string) => void;
    onBack: () => void;
}

export const BizBuildingApp = ({ onExecutionStarted, onBack }: BizBuildingAppProps) => {
    const {
        lessons,
        currentLessonId,
        missions,
        resources,
        nextUnlockIn,
        programStartDate,
        isLoading,
        error,
        completeTask,
        verifyMission,
        refresh,
        getCoachDashboard,
        bookSession,
        getLessonResources,
        approveVerification,
        getAvailability,
        cancelSession,
        rescheduleSession,
        getUserSessions,
        coachDashboard,
        coachDashboardLoading,
        forumThreads,
        forumThreadsLoading,
        getForumThreads,
        createForumThread,
        getForumReplies,
        postForumReply,
    } = useBizBuildingProgram();

    const { user } = useBilling();

    useEffect(() => {
        getForumThreads();
    }, [getForumThreads]);

    const [selectedLesson, setSelectedLesson] = useState<number | null>(null);
    const [showBooking, setShowBooking] = useState(false);

    const navItems = [
        { id: 'journey', label: 'Learning Journey', icon: BookOpen },
        { id: 'missions', label: 'Mission Center', icon: Award },
        { id: 'resources', label: 'Resource Hub', icon: FileText },
        { id: 'support', label: 'Coach Support', icon: HelpCircle },
        { id: 'community', label: 'Community', icon: MessageCircle },
    ];

    const role = (localStorage.getItem('USER_ROLE') || user?.role || 'CLIENT').toUpperCase();
    const isCoachOrAdmin = role === 'ADMIN' || role === 'COACH';

    if (isLoading) {
        return (
            <MiniAppShell title="BizBuilding Program" icon={GraduationCap} navItems={navItems} onBack={onBack}>
                {(activeTab, setActiveTab) => (
                    <div className="flex items-center justify-center h-full text-slate-400 text-xs font-medium animate-pulse">
                        Loading program state...
                    </div>
                )}
            </MiniAppShell>
        );
    }

    if (error) {
        return (
            <MiniAppShell title="BizBuilding Program" icon={GraduationCap} navItems={navItems} onBack={onBack}>
                {(activeTab, setActiveTab) => (
                    <div className="p-6 text-center space-y-4">
                        <div className="text-red-500 text-sm font-bold">Error loading program</div>
                        <p className="text-xs text-slate-400">{error}</p>
                        <button onClick={refresh} className="px-4 py-2 bg-slate-900 text-white rounded-lg text-[10px] uppercase font-bold">Retry</button>
                    </div>
                )}
            </MiniAppShell>
        );
    }

    const handleCompleteTask = async (taskId: string) => {
        try {
            await completeTask(taskId);
        } catch (e: any) {
            alert(e.message);
        }
    };

    const handleSelectLesson = (lessonId: number) => {
        setSelectedLesson(lessonId === selectedLesson ? null : lessonId);
    };

    const handleCloseLesson = () => {
        setSelectedLesson(null);
    };

    return (
        <MiniAppShell
            title="BizBuilding Program"
            icon={GraduationCap}
            navItems={navItems}
            onBack={onBack}
        >
            {(activeTab, setActiveTab) => {
                if (selectedLesson && activeTab === 'journey') {
                    return (
                        <LessonDetailView
                            lessons={lessons}
                            selectedLessonId={selectedLesson}
                            resources={resources}
                            onCompleteTask={handleCompleteTask}
                            onBack={handleCloseLesson}
                            onNavigate={handleSelectLesson}
                        />
                    );
                }

                switch (activeTab) {
                    case 'journey':
                        return (
                            <JourneyView
                                lessons={lessons}
                                currentLessonId={currentLessonId}
                                missions={missions}
                                onSelectLesson={handleSelectLesson}
                                onCompleteTask={handleCompleteTask}
                                userRole={role}
                                nextUnlockIn={nextUnlockIn}
                            />
                        );
                    case 'missions':
                        return <MissionCenterView missions={missions} onVerify={verifyMission} onExecutionStarted={onExecutionStarted} />;
                    case 'resources':
                        return <ResourceHubView resources={resources} />;
                    case 'community':
                        return (
                            <CommunityHub
                                threads={forumThreads as unknown as Thread[]}
                                isLoading={forumThreadsLoading}
                                onCreateThread={createForumThread}
                                onGetReplies={getForumReplies}
                                onPostReply={postForumReply}
                            />
                        );
                    case 'support':
                        return (
                            <SupportView
                                role={role}
                                isCoachOrAdmin={isCoachOrAdmin}
                                showBooking={showBooking}
                                setShowBooking={setShowBooking}
                                email={user?.email}
                                getCoachDashboard={getCoachDashboard}
                                bookSession={bookSession}
                                getLessonResources={getLessonResources}
                                approveVerification={approveVerification}
                                getAvailability={getAvailability}
                                cancelSession={cancelSession}
                                rescheduleSession={rescheduleSession}
                                getUserSessions={getUserSessions}
                                coachDashboard={coachDashboard}
                                coachDashboardLoading={coachDashboardLoading}
                            />
                        );
                    default:
                        return (
                            <JourneyView
                                lessons={lessons}
                                currentLessonId={currentLessonId}
                                missions={missions}
                                onSelectLesson={handleSelectLesson}
                                onCompleteTask={handleCompleteTask}
                                userRole={role}
                                nextUnlockIn={nextUnlockIn}
                            />
                        );
                }
            }}
        </MiniAppShell>
    );
};

const ProgressDashboard = ({ lessons, missions, currentLessonId, completedCount, progressPercent, nextUnlockIn }: { lessons: any[], missions: any[], currentLessonId: number | null, completedCount: number, progressPercent: number, nextUnlockIn: number | null }) => {
    const activeLesson = lessons.find(l => l.id === currentLessonId);
    const verifiedMissions = missions.filter((m: any) => m.Status === 'verified').length;
    const totalMissions = missions.length;

    return (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="p-5 bg-gradient-to-br from-amber-50 to-amber-100/50 border border-amber-200 rounded-2xl space-y-2">
                <div className="flex items-center gap-2 text-amber-600">
                    <Target size={16} />
                    <span className="text-[10px] font-black uppercase tracking-widest">Progress</span>
                </div>
                <div className="text-3xl font-black text-slate-800">{progressPercent}%</div>
                <div className="h-2 w-full bg-amber-200/50 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
                </div>
                <p className="text-[10px] text-slate-500 font-medium">{completedCount} of {lessons.length} lessons complete</p>
            </div>

            <div className="p-5 bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200 rounded-2xl space-y-2">
                <div className="flex items-center gap-2 text-blue-600">
                    <Zap size={16} />
                    <span className="text-[10px] font-black uppercase tracking-widest">Current</span>
                </div>
                <div className="text-sm font-black text-slate-800 truncate">{activeLesson?.title || 'N/A'}</div>
                <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${activeLesson?.status === 'active' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${activeLesson?.status === 'active' ? 'bg-amber-500 animate-pulse' : 'bg-slate-400'}`} />
                    {activeLesson?.status === 'active' ? 'In Progress' : 'Upcoming'}
                </div>
            </div>

            <div className="p-5 bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200 rounded-2xl space-y-2">
                <div className="flex items-center gap-2 text-emerald-600">
                    <Flag size={16} />
                    <span className="text-[10px] font-black uppercase tracking-widest">Missions</span>
                </div>
                <div className="text-3xl font-black text-slate-800">{verifiedMissions}/{totalMissions}</div>
                <p className="text-[10px] text-slate-500 font-medium">Verified achievements</p>
            </div>

            <div className="p-5 bg-gradient-to-br from-purple-50 to-purple-100/50 border border-purple-200 rounded-2xl space-y-2">
                <div className="flex items-center gap-2 text-purple-600">
                    <Clock size={16} />
                    <span className="text-[10px] font-black uppercase tracking-widest">Next Unlock</span>
                </div>
                <div className="text-3xl font-black text-slate-800">{nextUnlockIn !== null ? `${nextUnlockIn}d` : '--'}</div>
                <p className="text-[10px] text-slate-500 font-medium">{nextUnlockIn !== null ? `Next lesson in ${nextUnlockIn} day${nextUnlockIn === 1 ? '' : 's'}` : 'All lessons available'}</p>
            </div>
        </div>
    );
};

const LessonDetailView = ({ lessons, selectedLessonId, resources, onCompleteTask, onBack, onNavigate }: {
    lessons: any[];
    selectedLessonId: number;
    resources: any[];
    onCompleteTask: (id: string) => Promise<void>;
    onBack: () => void;
    onNavigate: (id: number) => void;
}) => {
    const lesson = lessons.find(l => l.id === selectedLessonId);
    if (!lesson) {return null;}

    const currentIndex = lessons.findIndex(l => l.id === selectedLessonId);
    const prevLesson = currentIndex > 0 ? lessons[currentIndex - 1] : null;
    const nextLesson = currentIndex < lessons.length - 1 ? lessons[currentIndex + 1] : null;

    const relatedResources = resources.filter((r: any) =>
        r.Lesson_ID === selectedLessonId || r.Tags?.includes(`lesson_${selectedLessonId}`)
    );

    const isActive = lesson.status === 'active';
    const isCompleted = lesson.status === 'completed';
    const isLocked = lesson.status === 'locked';

    const handleMarkComplete = async () => {
        await onCompleteTask(`task_${lesson.id}`);
    };

    const [showMobileNav, setShowMobileNav] = useState(false);

    return (
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-8 h-full animate-in fade-in slide-in-from-right-4 duration-300">
            {/* Left: Sidebar Navigation - hidden on mobile unless toggled */}
            <div className="hidden lg:block lg:w-56 shrink-0 space-y-4">
                <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors text-xs font-bold uppercase tracking-widest mb-4">
                    <ArrowLeft size={14} /> Back to Journey
                </button>

                <div className="space-y-1">
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Module Navigator</p>
                    <div className="border-t border-slate-100 pt-4 space-y-2 max-h-[60vh] overflow-y-auto">
                        {lessons.map((m, i) => {
                            const isSelected = m.id === selectedLessonId;
                            return (
                                <button
                                    key={m.id}
                                    onClick={() => onNavigate(m.id)}
                                    disabled={m.status === 'locked' && !isSelected}
                                    className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                                        isSelected
                                            ? 'bg-slate-900 text-white shadow-md'
                                            : m.status === 'locked'
                                            ? 'text-slate-300 cursor-not-allowed'
                                            : 'text-slate-600 hover:bg-slate-100'
                                    }`}
                                >
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black ${
                                        isSelected ? 'bg-white/20 text-white' :
                                        m.status === 'completed' ? 'bg-emerald-100 text-emerald-600' :
                                        m.status === 'locked' ? 'bg-slate-100 text-slate-300' : 'bg-amber-100 text-amber-600'
                                    }`}>
                                        {m.status === 'completed' ? <CheckCircle2 size={12} /> :
                                         m.status === 'locked' ? <Lock size={10} /> : i + 1}
                                    </div>
                                    <span className="text-[11px] font-bold truncate">{m.title}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Mobile Lesson Navigator Toggle */}
            <div className="lg:hidden flex items-center justify-between gap-3">
                <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors text-xs font-bold uppercase tracking-widest">
                    <ArrowLeft size={14} /> Back to Journey
                </button>
                <button onClick={() => setShowMobileNav(!showMobileNav)} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-[10px] font-bold uppercase tracking-widest">
                    {showMobileNav ? 'Hide Lessons' : 'All Lessons'}
                </button>
            </div>

            {/* Mobile Lesson Navigator Panel */}
            {showMobileNav && (
                <div className="lg:hidden bg-white border border-slate-100 rounded-2xl shadow-sm p-4 space-y-2 max-h-[40vh] overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-200">
                    {lessons.map((m, i) => {
                        const isSelected = m.id === selectedLessonId;
                        return (
                            <button
                                key={m.id}
                                onClick={() => { onNavigate(m.id); setShowMobileNav(false); }}
                                disabled={m.status === 'locked' && !isSelected}
                                className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                                    isSelected
                                        ? 'bg-slate-900 text-white shadow-md'
                                        : m.status === 'locked'
                                        ? 'text-slate-300 cursor-not-allowed'
                                        : 'text-slate-600 hover:bg-slate-100'
                                }`}
                            >
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black ${
                                    isSelected ? 'bg-white/20 text-white' :
                                    m.status === 'completed' ? 'bg-emerald-100 text-emerald-600' :
                                    m.status === 'locked' ? 'bg-slate-100 text-slate-300' : 'bg-amber-100 text-amber-600'
                                }`}>
                                    {m.status === 'completed' ? <CheckCircle2 size={12} /> :
                                     m.status === 'locked' ? <Lock size={10} /> : i + 1}
                                </div>
                                <span className="text-[11px] font-bold truncate">{m.title}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Center: Main Content */}
            <div className="flex-1 min-w-0 space-y-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="space-y-2 min-w-0">
                        <div className="flex items-center gap-3">
                            <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                                isCompleted ? 'bg-emerald-100 text-emerald-700' :
                                isActive ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-400'
                            }`}>
                                {isCompleted ? 'Completed' : isActive ? 'Active Lesson' : 'Locked'}
                            </div>
                            <span className="text-[10px] text-slate-400 font-medium">Lesson {lesson.id}</span>
                        </div>
                        <h2 className="text-xl lg:text-2xl font-black text-slate-800 uppercase tracking-tight truncate">{lesson.title}</h2>
                    </div>

                    {isActive && (
                        <button
                            onClick={handleMarkComplete}
                            className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-2xl text-xs font-bold uppercase tracking-widest hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-200 shrink-0"
                        >
                            <CheckCircle2 size={16} /> Mark Complete
                        </button>
                    )}
                </div>

                <div className="bg-white border border-slate-100 rounded-3xl shadow-sm p-4 lg:p-8 min-h-[300px] lg:min-h-[400px]">
                    {isLocked ? (
                        <div className="flex flex-col items-center justify-center h-full py-12 lg:py-16 text-center space-y-4">
                            <div className="p-6 bg-slate-100 rounded-full text-slate-400">
                                <Lock size={48} />
                            </div>
                            <h3 className="text-lg font-black text-slate-500 uppercase tracking-tight">Lesson Locked</h3>
                            <p className="text-sm text-slate-400 max-w-md">
                                {lesson.unlockReason || 'Complete the previous lesson to unlock this content. Each lesson builds on the last.'}
                            </p>
                        </div>
                    ) : (
                        <div className="prose prose-sm max-w-none">
                            {/* Video Placeholder */}
                            <div className="aspect-video bg-slate-900 rounded-2xl mb-8 flex items-center justify-center relative overflow-hidden group cursor-pointer">
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                                <div className="p-4 lg:p-6 bg-white/10 backdrop-blur-sm rounded-full group-hover:bg-white/20 transition-all">
                                    <Play size={32} className="text-white ml-1 lg:ml-0" />
                                </div>
                                <span className="absolute bottom-4 left-4 text-white/60 text-[10px] font-bold uppercase tracking-widest">Lesson Video</span>
                            </div>

                            <div className="space-y-6">
                                {lesson.content ? (
                                    <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                                        {lesson.content}
                                    </div>
                                ) : (
                                    <div className="text-center py-12 text-slate-400 text-sm font-medium">
                                        Lesson content is being prepared. Check back soon.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Mobile Prev/Next Navigation */}
                <div className="flex gap-2 lg:hidden">
                    {prevLesson && (
                        <button onClick={() => onNavigate(prevLesson.id)} className="flex-1 flex items-center justify-center gap-1 px-4 py-3 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-[10px] font-bold uppercase tracking-widest transition-all min-h-[44px]">
                            <ChevronLeft size={12} /> Prev
                        </button>
                    )}
                    {nextLesson && (
                        <button onClick={() => onNavigate(nextLesson.id)} className="flex-1 flex items-center justify-center gap-1 px-4 py-3 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-[10px] font-bold uppercase tracking-widest transition-all min-h-[44px]">
                            Next <ChevronRight size={12} />
                        </button>
                    )}
                </div>
            </div>

            {/* Right: Resources Sidebar - hidden on mobile */}
            <div className="hidden lg:block lg:w-64 shrink-0 space-y-4">
                <div className="p-4 bg-white border border-slate-100 rounded-2xl shadow-sm space-y-3">
                    <div className="flex items-center gap-2 text-slate-600">
                        <FileText size={14} />
                        <span className="text-[10px] font-black uppercase tracking-widest">Related Resources</span>
                    </div>
                    {relatedResources.length === 0 ? (
                        <p className="text-[10px] text-slate-400 font-medium py-2">No resources linked to this lesson.</p>
                    ) : (
                        <div className="space-y-2">
                            {relatedResources.map((r: any, i: number) => (
                                <div key={r.Resource_ID || i} className="p-3 bg-slate-50 rounded-xl space-y-1 hover:bg-amber-50 transition-colors cursor-pointer group">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[11px] font-bold text-slate-700 group-hover:text-amber-700 transition-colors">{r.Title || 'Resource'}</span>
                                        <Download size={12} className="text-slate-300 group-hover:text-amber-500 transition-colors shrink-0" />
                                    </div>
                                    <div className="flex items-center gap-2 text-[9px] text-slate-400 font-medium uppercase tracking-wider">
                                        <span className="px-1.5 py-0.5 bg-slate-200 rounded">{r.Type || 'Document'}</span>
                                        {r.Pages && <span>{r.Pages} pages</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl space-y-3">
                    <div className="flex items-center gap-2 text-amber-700">
                        <HelpCircle size={14} />
                        <span className="text-[10px] font-black uppercase tracking-widest">Need Help?</span>
                    </div>
                    <p className="text-[10px] text-amber-600/80 font-medium leading-relaxed">
                        Stuck on this lesson? Reach out to your coach or check the FAQ in Coach Support.
                    </p>
                    <button className="w-full py-2 bg-amber-600 text-white rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-amber-700 transition-all">
                        Ask Coach
                    </button>
                </div>
            </div>
        </div>
    );
};

const JourneyView = ({ lessons, currentLessonId, missions, onSelectLesson, onCompleteTask, userRole, nextUnlockIn }: {
    lessons: any[];
    currentLessonId: number | null;
    missions: any[];
    onSelectLesson: (id: number) => void;
    onCompleteTask: (id: string) => Promise<void>;
    userRole: string;
    nextUnlockIn: number | null;
}) => {
    const completedCount = lessons.filter(l => l.status === 'completed').length;
    const progressPercent = lessons.length > 0 ? Math.round((completedCount / lessons.length) * 100) : 0;

    const activeLesson = lessons.find(l => l.id === currentLessonId);

    return (
        <div className="space-y-8">
            <div className="flex items-center justify-between">
                <div className="space-y-1">
                    <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Your Learning Journey</h2>
                    <p className="text-xs text-slate-400 font-medium">Master the art of a corporatized business</p>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-100 rounded-full text-amber-600 text-[10px] font-black uppercase tracking-widest">
                    <Award size={12} /> Progress: {progressPercent}%
                </div>
            </div>

            {/* Welcome Video Banner */}
            <WelcomeVideoBanner />

            {/* Progress Dashboard */}
            <ProgressDashboard
                lessons={lessons}
                missions={missions}
                currentLessonId={currentLessonId}
                completedCount={completedCount}
                progressPercent={progressPercent}
                nextUnlockIn={nextUnlockIn}
            />

            {/* Active Lesson CTA */}
            {activeLesson && activeLesson.status === 'active' && (
                <div className="p-6 bg-gradient-to-r from-amber-50 to-amber-100/30 border border-amber-200 rounded-3xl flex items-center justify-between">
                    <div className="space-y-1">
                        <p className="text-[10px] text-amber-600 font-black uppercase tracking-widest">Continue Where You Left Off</p>
                        <h3 className="text-lg font-black text-slate-800">{activeLesson.title}</h3>
                    </div>
                    <button
                        onClick={() => onSelectLesson(activeLesson.id)}
                        className="flex items-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-2xl text-xs font-bold uppercase tracking-widest hover:bg-amber-700 transition-all shadow-lg shadow-amber-200"
                    >
                        <Play size={14} /> Resume Lesson
                    </button>
                </div>
            )}

            <div className="relative space-y-6 before:absolute before:left-6 before:top-0 before:bottom-0 before:w-0.5 before:bg-slate-200">
                {lessons.map((m, i) => (
                    <div key={m.id} className="relative pl-14 group">
                        <div className={`absolute left-4 top-1 w-4 h-4 rounded-full border-2 border-white shadow-sm transition-all ${
                            m.status === 'completed' ? 'bg-emerald-500' : m.status === 'active' ? 'bg-amber-500 animate-pulse' : 'bg-slate-300'
                        }`} />
                        <div
                            className={`p-6 bg-white border border-slate-100 rounded-3xl shadow-sm transition-all cursor-pointer ${
                                m.status === 'locked' ? 'opacity-60' : 'hover:border-amber-500 hover:shadow-md'
                            }`}
                            onClick={() => {
                                if (m.status !== 'locked') {
                                    onSelectLesson(m.id);
                                }
                            }}
                        >
                            <div className="flex items-center justify-between">
                                <div className="space-y-1 flex-1 min-w-0">
                                    <h3 className={`text-sm font-bold ${m.status === 'locked' ? 'text-slate-400' : 'text-slate-800'}`}>
                                        {m.title}
                                    </h3>
                                    <p className="text-[10px] text-slate-400 font-medium">
                                        {m.status === 'active' ? 'Current Module — Click to open' : m.status === 'completed' ? 'Module Complete' : m.unlockReason || 'Locked'}
                                    </p>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                    {m.status === 'active' && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onCompleteTask(`task_${m.id}`); }}
                                            className="px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-xl text-[10px] font-bold hover:bg-emerald-200 transition-all"
                                        >
                                            Quick Complete
                                        </button>
                                    )}
                                    {m.status === 'locked' ? <Lock size={16} className="text-slate-300" /> :
                                     m.status === 'completed' ? <CheckCircle2 size={16} className="text-emerald-500" /> :
                                     <ChevronRight size={16} className="text-amber-500" />}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const MissionCenterView = ({ missions, onVerify, onExecutionStarted }: { missions: any[], onVerify: (id: string) => Promise<void>, onExecutionStarted: (id: string) => void }) => {
    const handleVerify = async (missionId: string) => {
        try {
            await onVerify(missionId);
            alert('Verification audit triggered! We will notify you once your proof is validated.');
        } catch (err: any) {
            alert(err.message);
        }
    };

    const verifiedCount = missions.filter((m: any) => m.Status === 'verified').length;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="space-y-1">
                    <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Mission Center</h2>
                    <p className="text-xs text-slate-400 font-medium">Turn knowledge into verified achievements</p>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-100 rounded-full text-emerald-600 text-[10px] font-black uppercase tracking-widest">
                    <Award size={12} /> {verifiedCount}/{missions.length} Verified
                </div>
            </div>

            {missions.length > 0 && verifiedCount < missions.length && (
                <div className="p-4 bg-gradient-to-r from-amber-50 to-transparent border border-amber-200 rounded-2xl">
                    <p className="text-[10px] text-amber-700 font-bold">
                        <strong>Tip:</strong> Complete the associated lesson tasks first, then submit your proof for verification.
                    </p>
                </div>
            )}

            <div className="grid grid-cols-1 gap-4">
                {missions.length === 0 && (
                    <div className="text-center py-12 text-slate-400 text-xs font-medium">
                        No missions available at this time.
                    </div>
                )}
                {missions.map((m: any) => (
                    <div key={m.Mission_ID || m.id} className="p-6 bg-white border border-slate-100 rounded-3xl shadow-sm flex items-center justify-between group hover:border-amber-500 transition-all">
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-2xl ${
                                m.Status === 'verified' ? 'bg-emerald-50 text-emerald-600' :
                                m.Status === 'pending' ? 'bg-amber-50 text-amber-600' : 'bg-slate-50 text-slate-300'
                            }`}>
                                <Award size={20} />
                            </div>
                            <div className="space-y-1">
                                <h3 className="text-sm font-bold text-slate-700">{m.Title || m.title}</h3>
                                <div className="flex items-center gap-2">
                                    <p className="text-[10px] text-slate-400 font-medium">
                                        Reward: {m.Reward || 'Unlock next stage'}
                                    </p>
                                    <span className="text-slate-200">|</span>
                                    <p className="text-[10px] text-slate-400 font-medium">
                                        Proof: {m.Proof_type || m.Proof || 'Pending audit'}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => handleVerify(m.Mission_ID || m.id)}
                            disabled={m.Status === 'verified' || m.Status === 'locked'}
                            className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-all ${
                                m.Status === 'verified' ? 'bg-emerald-100 text-emerald-600 cursor-default' :
                                m.Status === 'locked' ? 'bg-slate-100 text-slate-300 cursor-not-allowed' : 'bg-slate-900 text-white hover:bg-slate-800'
                            }`}
                        >
                            {m.Status === 'verified' ? 'Verified' : m.Status === 'locked' ? 'Locked' : 'Verify Now'}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

const ResourceHubView = ({ resources }: { resources: any[] }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [activeCategory, setActiveCategory] = useState<string | null>(null);
    const [activeTags, setActiveTags] = useState<string[]>([]);

    const allResources = resources.map((r: any) => ({
        title: r.Title || r.title || 'Resource',
        type: r.Type || r.type || 'Document',
        tags: r.Tags || r.tags || '',
        id: r.Resource_ID || r.id || '',
    }));

    const categories = [...new Set(allResources.map(r => r.type))];

    const allTags = [...new Set(
        allResources.flatMap(r => r.tags ? r.tags.split(',').map((t: string) => t.trim()).filter(Boolean) : [])
    )].sort();

    const filtered = allResources.filter((r) => {
        const matchesSearch = !searchQuery || r.title.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = !activeCategory || r.type === activeCategory;
        const resourceTags = r.tags ? r.tags.split(',').map((t: string) => t.trim()).filter(Boolean) : [];
        const matchesTags = activeTags.length === 0 || activeTags.some(t => resourceTags.includes(t));
        return matchesSearch && matchesCategory && matchesTags;
    });

    const toggleTag = (tag: string) => {
        setActiveTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
    };

    const clearAllFilters = () => {
        setSearchQuery('');
        setActiveCategory(null);
        setActiveTags([]);
    };

    const hasActiveFilters = searchQuery || activeCategory || activeTags.length > 0;

    const tagColorMap: Record<string, string> = {
        onboarding: 'bg-sky-100 text-sky-700 border-sky-200',
        template: 'bg-violet-100 text-violet-700 border-violet-200',
        guide: 'bg-amber-100 text-amber-700 border-amber-200',
        playbook: 'bg-indigo-100 text-indigo-700 border-indigo-200',
        worksheet: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        video: 'bg-rose-100 text-rose-700 border-rose-200',
        reference: 'bg-slate-100 text-slate-700 border-slate-200',
    };

    const getTagStyle = (tag: string) => {
        const key = Object.keys(tagColorMap).find(k => tag.toLowerCase().includes(k));
        return key ? tagColorMap[key] : 'bg-slate-100 text-slate-600 border-slate-200';
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="space-y-1">
                    <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Resource Hub</h2>
                    <p className="text-xs text-slate-400 font-medium">All playbooks, templates and worksheets</p>
                </div>
            </div>

            {/* Search and Filter */}
            <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                    <input
                        type="text"
                        placeholder="Search resources..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full px-4 py-3 pl-10 bg-white border border-slate-200 rounded-2xl text-sm font-medium text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all"
                    />
                    <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
                    </svg>
                </div>
            </div>

            {/* Active filters indicator */}
            {hasActiveFilters && (
                <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-400 font-medium uppercase tracking-widest">Filters:</span>
                    {activeCategory && (
                        <span className="px-2.5 py-1 bg-slate-900 text-white rounded-lg text-[10px] font-bold uppercase tracking-widest">
                            {activeCategory}
                        </span>
                    )}
                    {activeTags.map(tag => (
                        <span key={tag} className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest cursor-pointer ${getTagStyle(tag)}`} onClick={() => toggleTag(tag)}>
                            {tag} ✕
                        </span>
                    ))}
                    <button
                        onClick={clearAllFilters}
                        className="px-2.5 py-1 text-[10px] text-slate-400 font-medium hover:text-slate-600 transition-colors uppercase tracking-widest"
                    >
                        Clear all
                    </button>
                </div>
            )}

            {/* Tag Filters */}
            {allTags.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                    {allTags.map(tag => (
                        <button
                            key={tag}
                            onClick={() => toggleTag(tag)}
                            className={`px-3 py-1.5 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all border ${
                                activeTags.includes(tag)
                                    ? 'bg-slate-900 text-white border-slate-900'
                                    : `${getTagStyle(tag)} hover:opacity-80`
                            }`}
                        >
                            {tag}
                        </button>
                    ))}
                </div>
            )}

            {/* Category Tabs */}
            {categories.length > 0 && (
                <div className="flex items-center gap-2 overflow-x-auto pb-2">
                    <button
                        onClick={() => setActiveCategory(null)}
                        className={`px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap ${
                            !activeCategory ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}
                    >
                        All
                    </button>
                    {categories.map(cat => (
                        <button
                            key={cat}
                            onClick={() => setActiveCategory(cat === activeCategory ? null : cat)}
                            className={`px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap ${
                                activeCategory === cat ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                            }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filtered.length === 0 && (
                    <div className="col-span-full text-center py-12 text-slate-400 text-xs font-medium">
                        {allResources.length === 0 ? 'No resources available.' : 'No resources match your search or filters.'}
                    </div>
                )}
                {filtered.map((r, i) => {
                    const resourceTags = r.tags ? r.tags.split(',').map((t: string) => t.trim()).filter(Boolean) : [];
                    return (
                        <div key={r.id || i} className="p-4 bg-white border border-slate-100 rounded-2xl shadow-sm group hover:border-amber-500 hover:shadow-md transition-all cursor-pointer">
                            <div className="aspect-video bg-slate-50 rounded-xl mb-3 flex items-center justify-center text-slate-300 group-hover:bg-amber-50 transition-colors">
                                <FileText size={32} />
                            </div>
                            <h3 className="text-xs font-bold text-slate-700 mb-1 group-hover:text-amber-700 transition-colors">{r.title}</h3>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[10px] text-slate-400 font-medium">{r.type}</span>
                            </div>
                            {resourceTags.length > 0 && (
                                <div className="flex items-center gap-1 flex-wrap">
                                    {resourceTags.map(tag => (
                                        <span key={tag} className={`px-1.5 py-0.5 rounded-md text-[8px] font-bold uppercase tracking-widest ${getTagStyle(tag)}`}>
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const SupportView = ({ role, isCoachOrAdmin, showBooking, setShowBooking, email, getCoachDashboard, bookSession, getLessonResources, approveVerification, getAvailability, cancelSession, rescheduleSession, getUserSessions, coachDashboard, coachDashboardLoading }: {
    role: string;
    isCoachOrAdmin: boolean;
    showBooking: boolean;
    setShowBooking: (v: boolean) => void;
    email: string;
    getCoachDashboard: (coachEmail: string) => Promise<any>;
    bookSession: (userEmail: string, coachEmail: string, scheduledAt: string) => Promise<any>;
    getLessonResources: (lessonId: number | string) => Promise<any>;
    approveVerification: (sessionId: string) => Promise<any>;
    getAvailability: (coachEmail: string, date: string) => Promise<any>;
    cancelSession: (userEmail: string, sessionId: string) => Promise<any>;
    rescheduleSession: (userEmail: string, sessionId: string, newScheduledAt: string) => Promise<any>;
    getUserSessions: (userEmail: string) => Promise<any>;
    coachDashboard: any;
    coachDashboardLoading: boolean;
}) => {
    const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
    const { setFaqContext } = useChat();
    const [bookingDate, setBookingDate] = useState('');
    const [bookingTime, setBookingTime] = useState('');
    const [bookingStatus, setBookingStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [expandedCoachSection, setExpandedCoachSection] = useState<string | null>(null);
    const [availableSlots, setAvailableSlots] = useState<{time: string; available: boolean}[]>([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
const [userSessions, setUserSessions] = useState<any[]>([]);
    const [userSessionsLoading, setUserSessionsLoading] = useState(false);
    const [reschedulingSession, setReschedulingSession] = useState<string | null>(null);
    const [rescheduleDate, setRescheduleDate] = useState('');
    const [rescheduleTime, setRescheduleTime] = useState('');
    const [cancellingSession, setCancellingSession] = useState<string | null>(null);
    const [rescheduleSlots, setRescheduleSlots] = useState<{time: string; available: boolean}[]>([]);
    const [rescheduleSlotsLoading, setRescheduleSlotsLoading] = useState(false);

    const loadUserSessions = async () => {
        if (!email || !getUserSessions) {return;}
        setUserSessionsLoading(true);
        try {
            const result = await getUserSessions(email);
            setUserSessions(result?.sessions || []);
        } catch {
            setUserSessions([]);
        } finally {
            setUserSessionsLoading(false);
        }
    };

    useEffect(() => {
        if (!isCoachOrAdmin && email) {
            loadUserSessions();
        }
    }, [isCoachOrAdmin, email]);

    useEffect(() => {
        if (!bookingDate || !getAvailability || !email) {
            setAvailableSlots([]);
            return;
        }
        setSlotsLoading(true);
        getAvailability(config.ADMIN_EMAIL || 'admin@chrysalinks.co.za', bookingDate)
            .then((res: any) => setAvailableSlots(res?.slots || []))
            .catch(() => setAvailableSlots([]))
            .finally(() => setSlotsLoading(false));
    }, [bookingDate, getAvailability, email]);

    useEffect(() => {
        if (!rescheduleDate || !getAvailability) {
            setRescheduleSlots([]);
            return;
        }
        setRescheduleSlotsLoading(true);
        getAvailability(config.ADMIN_EMAIL || 'admin@chrysalinks.co.za', rescheduleDate)
            .then((res: any) => setRescheduleSlots(res?.slots || []))
            .catch(() => setRescheduleSlots([]))
            .finally(() => setRescheduleSlotsLoading(false));
    }, [rescheduleDate, getAvailability]);

    useEffect(() => {
        if (expandedFaq !== null && faqs[expandedFaq]) {
            setFaqContext(`Q: ${faqs[expandedFaq].q}\nA: ${faqs[expandedFaq].a}`);
        } else {
            setFaqContext('');
        }
    }, [expandedFaq, setFaqContext]);

    useEffect(() => {
        if (isCoachOrAdmin && email && getCoachDashboard) {
            getCoachDashboard(email);
        }
    }, [isCoachOrAdmin, email, getCoachDashboard]);

    const faqs = [
        { q: 'How do I progress through the program?', a: 'Complete each lesson\'s tasks in order. Once a lesson is marked complete, the next lesson unlocks automatically.' },
        { q: 'What happens when I verify a mission?', a: 'Your proof is submitted for AI audit. Once validated, the mission is marked verified and you unlock associated rewards.' },
        { q: 'Can I reset my progress?', a: 'Contact your coach to request a progress reset. This will mark all lessons as incomplete and restart your journey.' },
        { q: 'How do I book a coaching session?', a: 'Use the booking calendar below to select a time slot. Sessions are 45 minutes via Google Meet.' },
        { q: 'What resources are available?', a: 'The Resource Hub contains playbooks, templates, deep dives, and worksheets tied to each lesson.' },
    ];

    if (isCoachOrAdmin) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div className="space-y-1">
                        <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Coach Dashboard</h2>
                        <p className="text-xs text-slate-400 font-medium">Manage your clients and their progress</p>
                    </div>
                    <div className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-[10px] font-black uppercase tracking-widest">
                        {role} Access
                    </div>
                </div>

                {coachDashboardLoading ? (
                    <div className="flex items-center justify-center py-12 text-slate-400 text-xs font-medium animate-pulse">
                        Loading coach dashboard...
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-6 bg-white border border-slate-100 rounded-3xl shadow-sm space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl w-fit">
                                        <Users size={24} />
                                    </div>
                                    <span className="px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-[10px] font-black">
                                        {coachDashboard?.clients?.length || 0}
                                    </span>
                                </div>
                                <h3 className="text-lg font-black text-slate-800">Client Roster</h3>
                                <p className="text-xs text-slate-400 font-medium">View and manage all your assigned clients' program progress, mission verification status, and engagement metrics.</p>
                                <button
                                    onClick={() => setExpandedCoachSection(expandedCoachSection === 'clients' ? null : 'clients')}
                                    className="px-4 py-2 bg-slate-900 text-white rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-slate-800 transition-all"
                                >
                                    {expandedCoachSection === 'clients' ? 'Hide Clients' : 'View Clients'}
                                </button>
                            </div>

                            <div className="p-6 bg-white border border-slate-100 rounded-3xl shadow-sm space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="p-3 bg-amber-50 text-amber-600 rounded-2xl w-fit">
                                        <Award size={24} />
                                    </div>
                                    <span className="px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-[10px] font-black">
                                        {coachDashboard?.pendingVerifications?.length || 0}
                                    </span>
                                </div>
                                <h3 className="text-lg font-black text-slate-800">Pending Verifications</h3>
                                <p className="text-xs text-slate-400 font-medium">Review and approve client mission submissions that are pending AI audit and manual verification.</p>
                                <button
                                    onClick={() => setExpandedCoachSection(expandedCoachSection === 'pending' ? null : 'pending')}
                                    className="px-4 py-2 bg-slate-900 text-white rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-slate-800 transition-all"
                                >
                                    {expandedCoachSection === 'pending' ? 'Hide Queue' : 'Review Queue'}
                                </button>
                            </div>

                            <div className="p-6 bg-white border border-slate-100 rounded-3xl shadow-sm space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="p-3 bg-emerald-50 text-emerald-600 rounded-2xl w-fit">
                                        <Calendar size={24} />
                                    </div>
                                    <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-black">
                                        {coachDashboard?.sessions?.length || 0}
                                    </span>
                                </div>
                                <h3 className="text-lg font-black text-slate-800">Schedule Overview</h3>
                                <p className="text-xs text-slate-400 font-medium">Manage your coaching calendar, upcoming sessions, and availability for client bookings.</p>
                                <button
                                    onClick={() => setExpandedCoachSection(expandedCoachSection === 'sessions' ? null : 'sessions')}
                                    className="px-4 py-2 bg-slate-900 text-white rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-slate-800 transition-all"
                                >
                                    {expandedCoachSection === 'sessions' ? 'Hide Calendar' : 'Manage Calendar'}
                                </button>
                            </div>
                        </div>

                        {/* Expanded Sections */}
                        {expandedCoachSection === 'clients' && (
                            <div className="p-6 bg-white border border-slate-100 rounded-3xl shadow-sm space-y-4">
                                <h4 className="text-xs font-black text-slate-800 uppercase tracking-tight">Assigned Clients</h4>
                                {(!coachDashboard?.clients || coachDashboard.clients.length === 0) ? (
                                    <p className="text-[11px] text-slate-400 font-medium">No clients assigned yet.</p>
                                ) : (
                                    <div className="space-y-3">
                                        {coachDashboard.clients.map((c: any, i: number) => (
                                            <div key={c.User_Email || i} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl">
                                                <div className="space-y-1">
                                                    <p className="text-xs font-bold text-slate-700">{c.User_Email || c.Name || 'Unnamed Client'}</p>
                                                    <p className="text-[10px] text-slate-400 font-medium">Started: {c.Started_At ? new Date(c.Started_At).toLocaleDateString() : 'N/A'}</p>
                                                </div>
                                                <span className={`px-2 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider ${c.Status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                                                    {c.Status || 'active'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {expandedCoachSection === 'pending' && (
                            <div className="p-6 bg-white border border-slate-100 rounded-3xl shadow-sm space-y-4">
                                <h4 className="text-xs font-black text-slate-800 uppercase tracking-tight">Pending Verifications</h4>
                                {(!coachDashboard?.pendingVerifications || coachDashboard.pendingVerifications.length === 0) ? (
                                    <p className="text-[11px] text-slate-400 font-medium">No pending verifications. All clear!</p>
                                ) : (
                                    <div className="space-y-3">
                                        {coachDashboard.pendingVerifications.map((s: any, i: number) => (
                                            <div key={s.Session_ID || i} className="flex items-center justify-between p-4 bg-amber-50 rounded-2xl">
                                                <div className="space-y-1">
                                                    <p className="text-xs font-bold text-slate-700">{s.User_Email || 'Unknown User'}</p>
                                                    <p className="text-[10px] text-slate-400 font-medium">Scheduled: {s.Scheduled_At ? new Date(s.Scheduled_At).toLocaleDateString() : 'N/A'}</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-[9px] font-bold uppercase tracking-wider">Pending</span>
                                                    <button
                                                        onClick={async () => {
                                                            try {
                                                                await approveVerification(s.Session_ID);
                                                            } catch (e: any) {
                                                                alert(e.message);
                                                            }
                                                        }}
                                                        className="px-3 py-1 bg-emerald-600 text-white rounded-xl text-[9px] font-bold uppercase tracking-wider hover:bg-emerald-700 transition-all"
                                                    >
                                                        Approve
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {expandedCoachSection === 'sessions' && (
                            <div className="p-6 bg-white border border-slate-100 rounded-3xl shadow-sm space-y-4">
                                <h4 className="text-xs font-black text-slate-800 uppercase tracking-tight">Upcoming Sessions</h4>
                                {(!coachDashboard?.sessions || coachDashboard.sessions.length === 0) ? (
                                    <p className="text-[11px] text-slate-400 font-medium">No upcoming sessions scheduled.</p>
                                ) : (
                                    <div className="space-y-3">
                                 {coachDashboard.sessions.map((s: any, i: number) => (
                                     <div key={s.Session_ID || i} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl">
                                         <div className="space-y-1">
                                             <p className="text-xs font-bold text-slate-700">{s.User_Email || 'Unknown User'}</p>
                                             <p className="text-[10px] text-slate-400 font-medium">
                                                 {s.Scheduled_At ? new Date(s.Scheduled_At).toLocaleString() : 'No date set'} &middot; {s.Duration_Minutes || 45} min
                                             </p>
                                             {s.Meeting_Link && (
                                                 <a href={s.Meeting_Link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] text-blue-600 font-bold hover:text-blue-800 transition-colors">
                                                     <Video size={12} /> Join Meet
                                                 </a>
                                             )}
                                         </div>
                                         <div className="flex items-center gap-3">
                                             <span className={`px-2 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider ${s.Status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                                 {s.Status || 'pending'}
                                             </span>
                                             <div className="flex gap-1">
                                                 <button
                                                     onClick={() => {
                                                         setReschedulingSession(s.Session_ID);
                                                         setRescheduleDate('');
                                                         setRescheduleTime('');
                                                     }}
                                                     className="flex-1 flex items-center justify-center gap-1 py-2 bg-slate-100 text-slate-600 rounded-xl text-[10px] font-bold hover:bg-amber-50 hover:text-amber-700 transition-all"
                                                 >
                                                     <RefreshCw size={12} />
                                                     Reschedule
                                                     </button>

                                                 <button
                                                     onClick={async () => {
                                                         if (confirm('Are you sure you want to cancel this session?')) {
                                                             try {
                                                                 await cancelSession(s.User_Email, s.Session_ID);
                                                                 await getCoachDashboard(email);
                                                             } catch (e: any) {
                                                                 alert(e.message);
                                                              }
                                                             }
                                                     }}
                                                     className="p-2 bg-white border border-slate-200 text-red-500 rounded-lg hover:bg-red-50 transition-all"
                                                     title="Cancel"
                                                 >
                                                     <XCircle size={12} />
                                                 </button>
                                             </div>
                                         </div>
                                     </div>
                                 ))}

                                    </div>
                                )}
                            </div>
                        )}

                        <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50/30 border border-blue-200 rounded-3xl">
                            <p className="text-xs text-blue-700 font-medium">
                                <strong>Coach Tip:</strong> Use the Modules Hub to launch automation workflows for your clients. Track progress in real-time via the Execution Tracker.
                            </p>
                        </div>
                    </>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="space-y-1">
                    <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Coach Support</h2>
                    <p className="text-xs text-slate-400 font-medium">Get help when you need it</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left: FAQ + AI Chat */}
                <div className="lg:col-span-2 space-y-6">
                    {/* FAQ Accordion */}
                    <div className="bg-white border border-slate-100 rounded-3xl shadow-sm p-6 space-y-4">
                        <div className="flex items-center gap-2">
                            <HelpCircle size={18} className="text-slate-600" />
                            <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">Frequently Asked Questions</h3>
                        </div>
                        <div className="space-y-2">
                            {faqs.map((faq, i) => (
                                <div key={i} className="border border-slate-100 rounded-2xl overflow-hidden">
                                    <button
                                        onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                                        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
                                    >
                                        <span className="text-xs font-bold text-slate-700">{faq.q}</span>
                                        <ChevronRight size={14} className={`text-slate-400 transition-transform ${expandedFaq === i ? 'rotate-90' : ''}`} />
                                    </button>
                                    {expandedFaq === i && (
                                        <div className="px-4 pb-4 text-[11px] text-slate-500 font-medium leading-relaxed">
                                            {faq.a}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* AI Chatbot Link */}
                    <div className="bg-gradient-to-r from-purple-50 to-purple-100/30 border border-purple-200 rounded-3xl p-6 space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-purple-100 text-purple-600 rounded-2xl">
                                <MessageCircle size={24} />
                            </div>
                            <div className="space-y-1">
                                <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">AI Program Assistant</h3>
                                <p className="text-[10px] text-slate-400 font-medium">Get instant answers about your program</p>
                            </div>
                        </div>
                        <p className="text-xs text-slate-600 font-medium leading-relaxed">
                            Use the AI chat widget (bottom-right corner) to ask questions about your lessons, get clarification on concepts, or request resource recommendations.
                        </p>
                        <div className="flex items-center gap-2 text-[10px] text-purple-600 font-bold">
                            <MessageCircle size={12} />
                            Click the chat bubble in the bottom-right to start
                        </div>
                    </div>
                </div>

                {/* Right: Booking + Resources */}
                <div className="space-y-6">
                    {/* 1-on-1 Booking */}
                    <div className="bg-white border border-slate-100 rounded-3xl shadow-sm p-6 space-y-4">
                        <div className="flex items-center gap-2">
                            <Calendar size={18} className="text-slate-600" />
                            <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">Book Strategy Call</h3>
                        </div>
                        <p className="text-[11px] text-slate-400 font-medium leading-relaxed">
                            Need a strategy pivot? Book a 45-minute 1-on-1 session with your assigned coach.
                        </p>

                        {showBooking ? (
                            <div className="space-y-4">
                                <div className="p-4 bg-slate-50 rounded-2xl space-y-3">
                                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Select Date</label>
                                    <BookingCalendar selectedDate={bookingDate} onDateSelect={setBookingDate} minDate={new Date().toISOString().split('T')[0]} />
                                </div>
                                <div className="p-4 bg-slate-50 rounded-2xl space-y-3">
                                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Select Time</label>
                                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                                        {slotsLoading ? (
                                            <div className="col-span-3 text-center py-3 text-[10px] text-slate-400 font-medium animate-pulse">Loading availability...</div>
                                        ) : availableSlots.length === 0 && bookingDate ? (
                                            <div className="col-span-3 text-center py-3 text-[10px] text-slate-400 font-medium">No slots available. Try another date.</div>
                                        ) : availableSlots.length === 0 ? (
                                            <div className="col-span-3 text-center py-3 text-[10px] text-slate-400 font-medium">Select a date to see available times.</div>
                                        ) : (
                                            availableSlots.map((s: {time: string; available: boolean}) => (
                                                <button key={s.time} onClick={() => s.available && setBookingTime(s.time)} disabled={!s.available} className={"min-h-[44px] px-3 py-2 rounded-xl text-[11px] font-bold transition-all " + (bookingTime === s.time ? "bg-amber-600 text-white border border-amber-600" : s.available ? "bg-white border border-slate-200 text-slate-600 hover:border-amber-500 hover:text-amber-700" : "bg-slate-100 border border-slate-200 text-slate-300 cursor-not-allowed line-through")}>
                                                    {s.time}
                                                </button>
                                            ))
                                        )}
                                    </div>
                                </div>
                                {bookingStatus && (
                                    <div className={`px-4 py-3 rounded-2xl text-[10px] font-bold ${bookingStatus.type === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                        {bookingStatus.type === 'success' && bookingStatus.message.includes('meet.google.com') ? (
                                            <span>Session booked! Join: <a href={bookingStatus.message.split('Join: ')[1]} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">{bookingStatus.message.split('Join: ')[1]}</a></span>
                                        ) : (
                                            bookingStatus.message
                                        )}
                                    </div>
                                )}
                                <button
                                    onClick={async () => {
                                        if (!bookingDate || !bookingTime) {
                                            setBookingStatus({ type: 'error', message: 'Please select both a date and time.' });
                                            return;
                                        }
                                        setBookingStatus(null);
                                        const scheduledAt = new Date(`${bookingDate}T${bookingTime}:00`).toISOString();
                                        try {
                                            const coachEmail = coachDashboard?.sessions?.[0]?.Coach_Email || email;
                                            const result = await bookSession(email, coachEmail, scheduledAt);
                                            const meetLink = result?.meetingLink || 'https://meet.google.com/';
                                            setBookingStatus({
                                                type: 'success',
                                                message: `Session booked! Join: ${meetLink}`,
                                            });
                                            setBookingDate('');
                                            setBookingTime('');
                                            setShowBooking(false);
                                        } catch (e: any) {
                                            setBookingStatus({ type: 'error', message: e.message || 'Failed to book session. Please try again.' });
                                        }
                                    }}
                                    className="w-full py-3 bg-amber-600 text-white rounded-2xl text-xs font-bold uppercase tracking-widest hover:bg-amber-700 transition-all shadow-lg shadow-amber-200"
                                >
                                    Confirm Booking
                                </button>
                                <button onClick={() => setShowBooking(false)} className="w-full text-[10px] text-slate-400 font-bold uppercase tracking-widest hover:text-slate-600 transition-all">
                                    Cancel
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => setShowBooking(true)}
                                className="w-full py-3 bg-slate-900 text-white rounded-2xl text-xs font-bold uppercase tracking-widest hover:bg-slate-800 transition-all"
                            >
                                Book a Session
                            </button>
                        )}
                    </div>

{/* My Sessions */}
                    <div className="bg-white border border-slate-100 rounded-3xl shadow-sm p-6 space-y-4">
                        <div className="flex items-center gap-2">
                            <Clock size={18} className="text-slate-600" />
                            <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">My Sessions</h3>
                        </div>
                        {userSessionsLoading ? (
                            <div className="text-center py-4 text-[10px] text-slate-400 font-medium animate-pulse">Loading sessions...</div>
                        ) : userSessions.length === 0 ? (
                            <p className="text-[11px] text-slate-400 font-medium">No sessions booked yet. Book your first strategy call above.</p>
                        ) : (
                            <div className="space-y-3">
                                {userSessions.map((s: any, i: number) => (
                                    <div key={s.Session_ID || i} className="p-4 bg-slate-50 rounded-2xl space-y-3">
                                        <div className="flex items-center justify-between">
                                            <div className="space-y-1">
                                                <p className="text-xs font-bold text-slate-700">
                                                    {s.Scheduled_At ? new Date(s.Scheduled_At).toLocaleString() : 'No date set'}
                                                </p>
                                                <p className="text-[10px] text-slate-400 font-medium">with {s.Coach_Email || 'your coach'}</p>
                                            </div>
                                            <span className={`px-2 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                                                s.Status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' :
                                                s.Status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                                'bg-amber-100 text-amber-700'
                                            }`}>
                                                {s.Status || 'pending'}
                                            </span>
                                        </div>
                                        {s.Meeting_Link && s.Status !== 'cancelled' && (
                                            <a href={s.Meeting_Link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] text-blue-600 font-bold hover:text-blue-800 transition-colors">
                                                <Video size={12} /> Join Meet
                                            </a>
                                        )}
                                        {(s.Status === 'pending' || s.Status === 'confirmed') && (
                                            <div className="flex gap-2 pt-1 border-t border-slate-200">
                                                <button
                                                    onClick={() => {
                                                        setReschedulingSession(s.Session_ID);
                                                        setRescheduleDate('');
                                                        setRescheduleTime('');
                                                    }}
                                                    className="flex-1 flex items-center justify-center gap-1 py-2 bg-slate-100 text-slate-600 rounded-xl text-[10px] font-bold hover:bg-amber-50 hover:text-amber-700 transition-all"
                                                >
                                                    <RefreshCw size={12} />
                                                    Reschedule
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        setCancellingSession(s.Session_ID);
                                                        try {
                                                            await cancelSession(email, s.Session_ID);
                                                            await loadUserSessions();
                                                        } catch (e: any) {
                                                            alert(e.message);
                                                        } finally {
                                                            setCancellingSession(null);
                                                        }
                                                    }}
                                                    disabled={cancellingSession === s.Session_ID}
                                                    className="flex-1 flex items-center justify-center gap-1 py-2 bg-slate-100 text-red-500 rounded-xl text-[10px] font-bold hover:bg-red-50 transition-all disabled:opacity-50"
                                                >
                                                    <XCircle size={12} />
                                                    {cancellingSession === s.Session_ID ? 'Cancelling...' : 'Cancel'}
                                                </button>
                                            </div>
                                        )}
                                         {reschedulingSession === s.Session_ID && (
                                             <div className="pt-4 border-t border-slate-200 space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                                 <div className="space-y-3">
                                                     <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Select New Date</p>
                                                     <BookingCalendar 
                                                         selectedDate={rescheduleDate} 
                                                         onDateSelect={setRescheduleDate} 
                                                         minDate={new Date().toISOString().split('T')[0]} 
                                                     />
                                                 </div>

                                                 <div className="space-y-3">
                                                     <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Available Slots</p>
                                                     <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                                         {rescheduleSlotsLoading ? (
                                                             <div className="col-span-4 text-center py-3 text-[10px] text-slate-400 font-medium animate-pulse">
                                                                 Loading availability...
                                                             </div>
                                                         ) : rescheduleSlots.length === 0 && rescheduleDate ? (
                                                             <div className="col-span-4 text-center py-3 text-[10px] text-slate-400 font-medium">
                                                                 No slots available for this date.
                                                             </div>
                                                         ) : rescheduleSlots.length === 0 ? (
                                                             <div className="col-span-4 text-center py-3 text-[10px] text-slate-400 font-medium">
                                                                 Select a date to see available times.
                                                             </div>
                                                         ) : (
                                                             rescheduleSlots.map((slot: {time: string; available: boolean}) => (
                                                                 <button 
                                                                     key={slot.time} 
                                                                     onClick={() => slot.available && setRescheduleTime(slot.time)} 
                                                                     disabled={!slot.available} 
                                                                     className={`min-h-[44px] px-2 py-2 rounded-lg text-[10px] font-bold transition-all ${
                                                                         rescheduleTime === slot.time 
                                                                             ? 'bg-amber-600 text-white shadow-sm' 
                                                                             : slot.available 
                                                                                 ? 'bg-white border border-slate-200 text-slate-600 hover:border-amber-500 hover:text-amber-700' 
                                                                                 : 'bg-slate-100 border border-slate-200 text-slate-300 cursor-not-allowed line-through'
                                                                     }`}
                                                                 >
                                                                     {slot.time}
                                                                 </button>
                                                              ))
                                                         )}
                                                     </div>
                                                 </div>

                                                 <div className="flex gap-2 pt-2">
                                                     <button
                                                         onClick={async () => {
                                                             if (!rescheduleDate || !rescheduleTime) {return;}
                                                             try {
                                                                 const newScheduledAt = new Date(`${rescheduleDate}T${rescheduleTime}:00`).toISOString();
                                                                 await rescheduleSession(email, s.Session_ID, newScheduledAt);
                                                                 setReschedulingSession(null);
                                                                 await loadUserSessions();
                                                             } catch (e: any) {
                                                                 alert(e.message);
                                                             }
                                                         }}
                                                         disabled={!rescheduleDate || !rescheduleTime}
                                                         className="flex-1 py-2 bg-amber-600 text-white rounded-xl text-[10px] font-bold uppercase hover:bg-amber-700 transition-all disabled:opacity-50"
                                                     >
                                                         Confirm Reschedule
                                                     </button>
                                                     <button
                                                         onClick={() => setReschedulingSession(null)}
                                                         className="px-4 py-2 text-[10px] text-slate-400 font-bold uppercase hover:text-slate-600 transition-all"
                                                     >
                                                         Cancel
                                                     </button>
                                                 </div>
                                             </div>
                                         )}

                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Quick Links */}
                    <div className="p-6 bg-gradient-to-br from-amber-50 to-amber-100/30 border border-amber-200 rounded-3xl space-y-3">
                        <div className="flex items-center gap-2">
                            <Zap size={16} className="text-amber-600" />
                            <h3 className="text-xs font-black text-slate-800 uppercase tracking-tight">Need Immediate Help?</h3>
                        </div>
                        <p className="text-[10px] text-amber-700/70 font-medium leading-relaxed">
                            Check the Resource Hub for playbooks and templates, or use the AI Assistant for instant answers.
                        </p>
                        <div className="flex gap-2 pt-2">
                            <span className="px-2 py-1 bg-white/60 rounded-lg text-[9px] font-bold text-amber-700 uppercase tracking-wider">📚 Resources</span>
                            <span className="px-2 py-1 bg-white/60 rounded-lg text-[9px] font-bold text-amber-700 uppercase tracking-wider">🤖 AI Chat</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
